import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateJobType, JobType, UpdateJobType } from '@thro-types/jobmarket';

import { AxiosRequestConfig } from 'axios';

import { createJobType, getJobType, getJobTypes, updateJobType } from 'api/jobmarketService';

/**
 * A custom hook for all JobTypes from the Jobmarket Service.
 *
 * @returns UseQueryResult<JobType[], Error>
 */
export const useJobTypesQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobTypes', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const jobTypes = getJobTypes(config);
      return jobTypes;
    },
    placeholderData: keepPreviousData,
    initialData: []
  });

  return query;
};

/**
 * A custom hook for one jobtype from the Jobmarket Service.
 *
 * @returns UseQueryResult<JobType[], Error>
 */
export const useJobTypeQuery = (id?: number, config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobTypes', id, config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      if (!id) {
        return;
      }
      const jobType = getJobType(id, config);
      return jobType;
    },
    enabled: !!id
  });

  return query;
};

export const useJobTypesMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['jobTypes'];
  const create = useMutation<JobType, Error, CreateJobType>({
    mutationFn: async args => {
      return await createJobType(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });
  const update = useMutation<JobType, Error, { id: number; data: UpdateJobType }>({
    mutationFn: async args => {
      return await updateJobType(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { update, create } as const;
};
